import {sendPasswordResetEmailToUser} from "../api/auth";
import {addUser, deleteUser, updateUser} from "../api/user";
import {USER_MODAL_CONTENT} from "../components/UserModalContent";
import {UserOrganization} from "../model/types";

type UseUserMutationsProps = {
  userToken: string;
  newUser: any;
  selectedRole: string;
  editingUser: UserOrganization | null;
  editedName: string;
  editedRole: string;
  editedPermissions: any;
  editedOrganizations: any;
  fetchUsers: () => Promise<void>;
  setDialogContent: (value: USER_MODAL_CONTENT | "") => void;
  setMessage: (value: string) => void;
  setMessageType: (value: "success" | "fail") => void;
  setEditingUser: (value: UserOrganization | null) => void;
};

export const useUserMutations = ({
  userToken,
  newUser,
  selectedRole,
  editingUser,
  editedName,
  editedRole,
  editedPermissions,
  editedOrganizations,
  fetchUsers,
  setDialogContent,
  setMessage,
  setMessageType,
  setEditingUser,
}: UseUserMutationsProps) => {
  const handleAddUser = async () => {
    if (!newUser.name || !newUser.email) return;
    setDialogContent(USER_MODAL_CONTENT.LOADING);

    try {
      const data = await addUser({token: userToken, user: newUser, selectedRole});
      if (!data.success) throw new Error(data.message);
      await sendPasswordResetEmailToUser(newUser.email);
      await fetchUsers();
      setDialogContent(USER_MODAL_CONTENT.MESSAGE);
      setMessage("帳號新增成功，請使用者收信以重設密碼！");
      setMessageType("success");
    } catch (error) {
      setDialogContent(USER_MODAL_CONTENT.MESSAGE);
      setMessage(error.message);
      setMessageType("fail");
    }
  };

  const handleUpdateUser = async () => {
    setDialogContent(USER_MODAL_CONTENT.LOADING);
    if (!editingUser || !editedName) return;
    try {
      await updateUser({
        token: userToken,
        user: {
          id: editingUser.id,
          name: editedName,
          role: editedRole,
          permissions: editedPermissions,
          organization: editedOrganizations.id
        }
      });
      await fetchUsers();
      setEditingUser(null);
      setDialogContent(USER_MODAL_CONTENT.MESSAGE);
      setMessage("帳號已編輯成功！");
      setMessageType("success");
    } catch (error) {
      setDialogContent(USER_MODAL_CONTENT.MESSAGE);
      setMessage(error.message);
      setMessageType("fail");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setDialogContent(USER_MODAL_CONTENT.LOADING);
    try {
      await deleteUser({token: userToken, uid: userId});
      await fetchUsers();
      setDialogContent(USER_MODAL_CONTENT.MESSAGE);
      setMessage("帳號已成功刪除！");
      setMessageType("success");
    } catch (error) {
      setDialogContent(USER_MODAL_CONTENT.MESSAGE);
      setMessage(error.message);
      setMessageType("fail");
    }
  };

  const handleResetPassword = async (user: UserOrganization) => {
    const email = user.email;
    if (!email) return alert("Invalid email address.");
    setDialogContent(USER_MODAL_CONTENT.LOADING);
    try {
      await sendPasswordResetEmailToUser(email);
      setDialogContent(USER_MODAL_CONTENT.MESSAGE);
      setMessage("已再次寄出密碼重設信，請提醒使用者收信！");
      setMessageType("success");
    } catch (error) {
      setDialogContent(USER_MODAL_CONTENT.MESSAGE);
      setMessage(error.message);
      setMessageType("fail");
    }
  };

  return {
    handleAddUser,
    handleUpdateUser,
    handleDeleteUser,
    handleResetPassword,
  };
};
